/**
 * Human-readable display names for work-context role codes.
 *
 * The session API only carries the technical `roleCode` (the seed's persona
 * key); showing that raw string in the header made every context read like a
 * database column. This catalogue localizes the codes the demo environment
 * seeds; unknown codes degrade to a humanized form of the code itself, so a
 * newly introduced role is never hidden — only unstyled.
 */

interface WorkContextLabel {
  readonly de: string;
  readonly en: string;
}

const LABELS: Readonly<Record<string, WorkContextLabel>> = {
  access_administrator: { de: "Zugriffsverwaltung", en: "Access administration" },
  account_holder: { de: "Kontoinhaber:in", en: "Account holder" },
  auditor: { de: "Revision", en: "Auditor" },
  collaboration_member: { de: "Einsatzkanäle", en: "Collaboration member" },
  collaboration_moderator: { de: "Kanal-Moderation", en: "Collaboration moderator" },
  command_context_administrator: { de: "Führungskontext-Verwaltung", en: "Command context administration" },
  configuration_approver: { de: "Konfigurationsfreigabe", en: "Configuration approver" },
  configuration_proposer: { de: "Konfigurationsvorschlag", en: "Configuration proposer" },
  document_reviewer: { de: "Dokumentenprüfung", en: "Document reviewer" },
  document_uploader: { de: "Dokumentenablage", en: "Document uploader" },
  etb_keeper: { de: "ETB-Führung", en: "ETB keeper" },
  etb_promotion_proposer: { de: "ETB-Übernahmevorschlag", en: "ETB promotion proposer" },
  etb_reviewer: { de: "ETB-Prüfung", en: "ETB reviewer" },
  field_helfer: { de: "Helfer:in im Einsatz", en: "Field helper" },
  field_user: { de: "Einsatzkraft", en: "Field responder" },
  form_approver: { de: "Formularfreigabe", en: "Form approver" },
  form_submitter: { de: "Formularerfassung", en: "Form submitter" },
  incident_commander: { de: "Einsatzleitung (EL)", en: "Incident commander" },
  logistics_processor: { de: "Logistikbearbeitung", en: "Logistics processor" },
  luk_activation_administrator: { de: "LuK-Aktivierung", en: "LuK activation administration" },
  luk_dispatcher: { de: "LuK-Disponent:in", en: "LuK dispatcher" },
  luk_operator: { de: "LuK-Sachbearbeitung", en: "LuK operator" },
  lv_admin: { de: "LV-Administration", en: "State association administration" },
  master_data_admin: { de: "Stammdatenverwaltung", en: "Master data administration" },
  mission_coordinator: { de: "Einsatzkoordination", en: "Mission coordinator" },
  notification_recipient: { de: "Benachrichtigungen", en: "Notification recipient" },
  operational_planner: { de: "Einsatzplanung", en: "Operational planner" },
  order_issuer: { de: "Auftragserteilung", en: "Order issuer" },
  order_recipient: { de: "Auftragsannahme", en: "Order recipient" },
  ortsbeauftragter: { de: "Ortsbeauftragte:r (OB)", en: "Local commissioner (OB)" },
  ov_admin: { de: "OV-Administration", en: "Local section administration" },
  personnel_data_steward: { de: "Personaldatenpflege", en: "Personnel data steward" },
  qualification_steward: { de: "Qualifikationspflege", en: "Qualification steward" },
  readiness_decision_maker: { de: "Bereitschaftsentscheidung", en: "Readiness decisions" },
  records_officer: { de: "Registratur", en: "Records officer" },
  reference_data_steward: { de: "Referenzdatenpflege", en: "Reference data steward" },
  regionalstelle: { de: "Regionalstelle", en: "Regional office" },
  registration_officer: { de: "Registrierung / Meldewesen", en: "Registration officer" },
  report_exporter: { de: "Berichtsexport", en: "Report exporter" },
  report_viewer: { de: "Berichtseinsicht", en: "Report viewer" },
  request_originator: { de: "Anforderungsstellung", en: "Request originator" },
  request_processor: { de: "Anforderungsbearbeitung", en: "Request processor" },
  resource_allocator: { de: "Ressourcendisposition", en: "Resource allocation" },
  rst_admin: { de: "RSt-Administration", en: "Regional office administration" },
  s1_personnel: { de: "S1 – Personal", en: "S1 – Personnel" },
  s2_situation_reporting: { de: "S2 – Lage", en: "S2 – Situation" },
  s3_operations: { de: "S3 – Einsatz", en: "S3 – Operations" },
  s4_logistics: { de: "S4 – Logistik", en: "S4 – Logistics" },
  s6_communications: { de: "S6 – IuK", en: "S6 – Communications" },
  schirrmeister: { de: "Schirrmeister:in", en: "Equipment master" },
  security_auditor: { de: "Sicherheitsrevision", en: "Security auditor" },
  security_operator: { de: "Sicherheitsbetrieb", en: "Security operations" },
  speech_configuration_administrator: { de: "Sprachdienst-Verwaltung", en: "Speech service administration" },
  super_admin: { de: "Systemverwaltung", en: "System administration" },
  superuser: { de: "Systemverwaltung", en: "System administration" },
  tactical_icon_publisher: { de: "Taktische Zeichen", en: "Tactical icon publishing" },
  technical_adviser: { de: "Fachberater:in", en: "Technical adviser" },
  travel_authorizer: { de: "Marschgenehmigung", en: "Travel authorization" },
  unit_leader: { de: "Einheitsführung", en: "Unit leader" },
  zugfuehrer: { de: "Zugführer:in", en: "Platoon leader" },
};

/** Resolves the localized display name for a work-context role code. */
export function workContextLabel(roleCode: string, locale: "de" | "en"): string {
  const known = LABELS[roleCode];
  if (known !== undefined) return known[locale];
  const words = roleCode.replace(/_/gu, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}
